import { create } from 'zustand';
import { amlApi } from '../services/amlApi';
import type {
  AmlMe, AmlCustomer, AmlTransaction,
  AmlLocation, AmlClient, AmlPivotRow, AmlUser, AmlSection,
} from '../types/aml';

interface AmlState {
  activeView: AmlSection;
  me: AmlMe | null;

  customers: AmlCustomer[];
  customersTotal: number;
  customersPage: number;
  customersSearch: string;
  customersPageSize: number;
  customersSort: string;
  customersSortDir: 'asc' | 'desc';
  selectedCustomer: AmlCustomer | null;
  customersLoading: boolean;

  transactions: AmlTransaction[];
  transactionsTotal: number;
  transactionsPage: number;
  transactionFilters: {
    customer_id?: string;
    location_id?: string;
    date_from?: string;
    date_to?: string;
  };
  selectedTransaction: AmlTransaction | null;
  transactionsPageSize: number;
  transactionsSort: string;
  transactionsSortDir: 'asc' | 'desc';
  transactionsLoading: boolean;

  locations: AmlLocation[];
  customersForFilter: AmlCustomer[];

  clients: AmlClient[];

  pivotRows: AmlPivotRow[];
  pivotFilters: { date_from?: string; date_to?: string; by_client?: string };

  amlUsers: AmlUser[];

  loading: boolean;
  error: string | null;
  showForm: string | null;
  showImport: boolean;
}

interface AmlActions {
  setActiveView: (view: AmlSection) => void;
  fetchMe: () => Promise<void>;

  fetchCustomers: (page?: number) => Promise<void>;
  setCustomersSearch: (search: string) => void;
  setCustomersPageSize: (size: number) => void;
  setCustomersSort: (field: string, dir: 'asc' | 'desc') => void;
  selectCustomer: (c: AmlCustomer | null) => void;
  createCustomer: (data: any) => Promise<void>;
  updateCustomer: (id: string, data: any) => Promise<void>;
  updateCustomerPartial: (id: string, data: Partial<AmlCustomer>) => Promise<AmlCustomer>;
  deleteCustomer: (id: string) => Promise<void>;
  refreshSelectedCustomer: () => Promise<void>;

  fetchTransactions: (page?: number) => Promise<void>;
  setTransactionFilters: (f: AmlState['transactionFilters']) => void;
  setTransactionsPageSize: (size: number) => void;
  setTransactionsSort: (field: string, dir: 'asc' | 'desc') => void;
  selectTransaction: (t: AmlTransaction | null) => void;
  createTransaction: (data: any) => Promise<void>;
  updateTransaction: (id: string, data: any) => Promise<void>;
  deleteTransaction: (id: string) => Promise<void>;
  bulkDeleteTransactions: (ids: string[]) => Promise<number>;
  importTransactions: (file: File) => Promise<{ imported: number; errors: string[] }>;

  fetchLocations: (customerId?: string) => Promise<void>;
  fetchCustomersForFilter: (force?: boolean) => Promise<void>;

  fetchClients: () => Promise<void>;
  createClient: (data: any) => Promise<void>;
  updateClient: (id: string, data: any) => Promise<void>;
  deleteClient: (id: string) => Promise<void>;
  uploadClientLogo: (clientId: string, file: File) => Promise<void>;

  fetchPivotReport: () => Promise<void>;
  setPivotFilters: (f: AmlState['pivotFilters']) => void;

  fetchAmlUsers: () => Promise<void>;
  createAmlUser: (data: {
    email: string; first_name?: string; last_name?: string;
    password?: string; system_role?: string; aml_client_id?: string; is_active?: boolean;
  }) => Promise<void>;
  updateAmlUser: (id: string, data: {
    first_name?: string; last_name?: string; password?: string;
    system_role?: string; aml_client_id?: string; is_active?: boolean;
  }) => Promise<void>;
  updateUserAmlRole: (userId: string, role: string | null) => Promise<void>;
  deleteAmlUser: (id: string) => Promise<void>;
  requestPasswordReset: (userId: string) => Promise<{ success: boolean; message: string }>;
  resetUserPassword: (userId: string, data: { password: string; confirm_password: string }) => Promise<{ success: boolean; message: string }>;
  impersonateUser: (userId: string) => Promise<{ access_token: string; id_token: string; refresh_token: string; email: string }>;

  setShowForm: (form: string | null) => void;
  setShowImport: (show: boolean) => void;
  clearError: () => void;
}

// Inflight trackers — shared across all store consumers.
// Each keyed fetcher gets its own slot so concurrent calls
// to the SAME endpoint share one network request.
const _inflight: Record<string, Promise<void>> = {};

function pageAfterDelete(
  state: Pick<AmlState, 'transactionsPage' | 'transactionsPageSize' | 'transactionsTotal'>,
  deletedCount: number,
): number {
  const remaining = Math.max(0, state.transactionsTotal - deletedCount);
  const lastPage = Math.max(1, Math.ceil(remaining / state.transactionsPageSize));
  return Math.min(state.transactionsPage, lastPage);
}

export const useAmlStore = create<AmlState & AmlActions>((set, get) => ({
  activeView: 'customers',
  me: null,
  customers: [],
  customersTotal: 0,
  customersPage: 1,
  customersSearch: '',
  customersPageSize: 20,
  customersSort: 'name',
  customersSortDir: 'asc' as const,
  selectedCustomer: null,
  customersLoading: false,
  transactions: [],
  transactionsTotal: 0,
  transactionsPage: 1,
  transactionFilters: {},
  selectedTransaction: null,
  transactionsPageSize: 20,
  transactionsSort: 'transaction_date',
  transactionsSortDir: 'desc' as const,
  transactionsLoading: false,
  locations: [],
  customersForFilter: [],
  clients: [],
  pivotRows: [],
  pivotFilters: { by_client: 'yes' },
  amlUsers: [],
  loading: false,
  error: null,
  showForm: null,
  showImport: false,

  setActiveView: (view) => set({ activeView: view }),

  // ── Me ──
  fetchMe: () => {
    if (get().me) return Promise.resolve();
    const key = 'me';
    if (_inflight[key]) return _inflight[key];
    _inflight[key] = (async () => {
      try {
        const me = await amlApi.getMe();
        set({ me });
      } catch (e: any) {
        set({ error: e?.response?.data?.detail || 'Failed to load AML context' });
      } finally {
        delete _inflight[key];
      }
    })();
    return _inflight[key];
  },

  // ── Customers ──
  fetchCustomers: (page?: number) => {
    const p = page ?? get().customersPage;
    const key = `customers:${p}:${get().customersSearch}:${get().customersSort}:${get().customersSortDir}:${get().customersPageSize}`;
    if (_inflight[key]) return _inflight[key];
    _inflight[key] = (async () => {
      try {
        const isInitial = get().customers.length === 0;
        if (isInitial) set({ loading: true });
        set({ customersLoading: true });
        const { items, total } = await amlApi.listCustomers({
          page: p,
          page_size: get().customersPageSize,
          search: get().customersSearch || undefined,
          order_by: get().customersSort,
          order_dir: get().customersSortDir,
        });
        set({ customers: items, customersTotal: total, customersPage: p, loading: false, customersLoading: false });
      } catch (e: any) {
        set({ loading: false, customersLoading: false, error: e?.response?.data?.detail || 'Failed to load customers' });
      } finally {
        delete _inflight[key];
      }
    })();
    return _inflight[key];
  },

  setCustomersSearch: (search) => set({ customersSearch: search }),
  setCustomersPageSize: (size) => set({ customersPageSize: size, customersPage: 1 }),
  setCustomersSort: (field, dir) => set({ customersSort: field, customersSortDir: dir, customersPage: 1 }),

  selectCustomer: (c) => set({ selectedCustomer: c }),

  createCustomer: async (data) => {
    set({ loading: true });
    try {
      await amlApi.createCustomer(data);
    } catch (e) {
      set({ loading: false });
      throw e;
    }
    set({ showForm: null, loading: false });
    await get().fetchCustomers();
    // Screening runs in the background — refresh once so the status column updates.
    window.setTimeout(() => {
      get().fetchCustomers();
    }, 2500);
  },

  updateCustomer: async (id, data) => {
    set({ loading: true });
    let updated;
    try {
      updated = await amlApi.updateCustomer(id, data);
    } catch (e) {
      set({ loading: false });
      throw e;
    }
    set({ showForm: null, loading: false, selectedCustomer: updated });
    get().fetchCustomers();
  },

  updateCustomerPartial: async (id, data) => {
    const updated = await amlApi.updateCustomer(id, data);
    set({ selectedCustomer: updated });
    return updated;
  },

  refreshSelectedCustomer: async () => {
    const sel = get().selectedCustomer;
    if (!sel) return;
    try {
      const fresh = await amlApi.getCustomer(sel.id);
      set({ selectedCustomer: fresh });
    } catch { /* non-critical */ }
  },

  deleteCustomer: async (id) => {
    set({ loading: true });
    await amlApi.deleteCustomer(id);
    set({ selectedCustomer: null, loading: false });
    get().fetchCustomers();
  },

  // ── Transactions ──
  fetchTransactions: (page?: number) => {
    const p = page ?? get().transactionsPage;
    const f = get().transactionFilters;
    const key = `txn:${p}:${get().transactionsSort}:${get().transactionsSortDir}:${get().transactionsPageSize}:${f.customer_id}:${f.location_id}:${f.date_from}:${f.date_to}`;
    if (_inflight[key]) return _inflight[key];
    _inflight[key] = (async () => {
      try {
        const isInitial = get().transactions.length === 0;
        if (isInitial) set({ loading: true });
        set({ transactionsLoading: true });
        const { items, total } = await amlApi.listTransactions({
          page: p,
          page_size: get().transactionsPageSize,
          order_by: get().transactionsSort,
          order_dir: get().transactionsSortDir,
          ...f,
        });
        set({ transactions: items, transactionsTotal: total, transactionsPage: p, loading: false, transactionsLoading: false });
      } catch (e: any) {
        set({ loading: false, transactionsLoading: false, error: e?.response?.data?.detail || 'Failed to load transactions' });
      } finally {
        delete _inflight[key];
      }
    })();
    return _inflight[key];
  },

  setTransactionFilters: (f) => set({ transactionFilters: f }),
  setTransactionsPageSize: (size) => set({ transactionsPageSize: size, transactionsPage: 1 }),
  setTransactionsSort: (field, dir) => set({ transactionsSort: field, transactionsSortDir: dir, transactionsPage: 1 }),

  selectTransaction: (t) => set({ selectedTransaction: t }),

  createTransaction: async (data) => {
    set({ loading: true });
    await amlApi.createTransaction(data);
    set({ showForm: null, loading: false });
    get().fetchTransactions();
  },

  updateTransaction: async (id, data) => {
    set({ loading: true });
    await amlApi.updateTransaction(id, data);
    set({ showForm: null, loading: false });
    get().fetchTransactions();
  },

  deleteTransaction: async (id) => {
    set({ loading: true });
    await amlApi.deleteTransaction(id);
    set({ selectedTransaction: null, loading: false });
    get().fetchTransactions(pageAfterDelete(get(), 1));
  },

  bulkDeleteTransactions: async (ids) => {
    set({ loading: true });
    const { deleted } = await amlApi.bulkDeleteTransactions(ids);
    set({ selectedTransaction: null, loading: false });
    get().fetchTransactions(pageAfterDelete(get(), deleted));
    return deleted;
  },

  importTransactions: async (file) => {
    set({ loading: true });
    const result = await amlApi.importTransactions(file);
    set({ loading: false });
    get().fetchTransactions();
    return result;
  },

  // ── Locations ──
  fetchLocations: (customerId?: string) => {
    const key = `locations:${customerId ?? 'all'}`;
    if (_inflight[key]) return _inflight[key];
    _inflight[key] = (async () => {
      try {
        const locations = await amlApi.listLocations(customerId ? { customer_id: customerId } : undefined);
        set({ locations });
      } catch {
        /* non-critical */
      } finally {
        delete _inflight[key];
      }
    })();
    return _inflight[key];
  },

  // ── Customers for filter dropdown ──
  fetchCustomersForFilter: (force) => {
    const key = 'customersForFilter';
    if (_inflight[key]) return _inflight[key];
    if (!force && get().customersForFilter.length > 0) return Promise.resolve();
    _inflight[key] = (async () => {
      try {
        const { items } = await amlApi.listCustomers({ page: 1, page_size: 500 });
        set({ customersForFilter: items });
      } catch {
        /* non-critical */
      } finally {
        delete _inflight[key];
      }
    })();
    return _inflight[key];
  },

  // ── Clients ──
  fetchClients: () => {
    const key = 'clients';
    if (_inflight[key]) return _inflight[key];
    set({ loading: true });
    _inflight[key] = (async () => {
      try {
        const clients = await amlApi.listClients();
        set({ clients });
      } catch (e: any) {
        set({ error: e?.response?.data?.detail || 'Failed to load clients' });
      } finally {
        set({ loading: false });
        delete _inflight[key];
      }
    })();
    return _inflight[key];
  },

  createClient: async (data) => {
    set({ loading: true });
    await amlApi.createClient(data);
    set({ showForm: null, loading: false });
    get().fetchClients();
  },

  updateClient: async (id, data) => {
    set({ loading: true });
    await amlApi.updateClient(id, data);
    set({ showForm: null, loading: false });
    get().fetchClients();
  },

  deleteClient: async (id) => {
    set({ loading: true });
    await amlApi.deleteClient(id);
    set({ loading: false });
    get().fetchClients();
  },

  uploadClientLogo: async (clientId, file) => {
    set({ loading: true });
    const updated = await amlApi.uploadClientLogo(clientId, file);
    set((state) => ({
      loading: false,
      clients: state.clients.map(c => c.id === clientId ? updated : c),
    }));
  },

  // ── Pivot Report ──
  fetchPivotReport: () => {
    const f = get().pivotFilters;
    const key = `pivot:${f.date_from}:${f.date_to}`;
    if (_inflight[key]) return _inflight[key];
    _inflight[key] = (async () => {
      try {
        set({ loading: true });
        const rows = await amlApi.getPivotReport(f);
        set({ pivotRows: rows, loading: false });
      } catch (e: any) {
        set({ loading: false, error: e?.response?.data?.detail || 'Failed to load pivot report' });
      } finally {
        delete _inflight[key];
      }
    })();
    return _inflight[key];
  },

  setPivotFilters: (f) => set({ pivotFilters: f }),

  // ── AML Users ──
  fetchAmlUsers: () => {
    const key = 'amlUsers';
    if (_inflight[key]) return _inflight[key];
    const isInitial = get().amlUsers.length === 0;
    _inflight[key] = (async () => {
      try {
        if (isInitial) set({ loading: true });
        const users = await amlApi.listAmlUsers();
        set({ amlUsers: users, loading: false });
      } catch {
        set({ loading: false });
      } finally {
        delete _inflight[key];
      }
    })();
    return _inflight[key];
  },

  createAmlUser: async (data) => {
    try {
      await amlApi.createAmlUser(data);
      set({ showForm: null });
      get().fetchAmlUsers();
    } catch (e: any) {
      throw e;
    }
  },

  updateAmlUser: async (id, data) => {
    try {
      await amlApi.updateAmlUser(id, data);
      set({ showForm: null });
      get().fetchAmlUsers();
    } catch (e: any) {
      throw e;
    }
  },

  updateUserAmlRole: async (userId, role) => {
    await amlApi.updateUserAmlRole(userId, role);
    get().fetchAmlUsers();
  },

  deleteAmlUser: async (id) => {
    set({ loading: true });
    try {
      await amlApi.deleteAmlUser(id);
      set({ loading: false });
      get().fetchAmlUsers();
    } catch (e: any) {
      set({ loading: false });
      throw e;
    }
  },

  requestPasswordReset: async (userId) => {
    const result = await amlApi.requestPasswordReset(userId);
    return result;
  },

  resetUserPassword: async (userId, data) => {
    const result = await amlApi.resetUserPassword(userId, data);
    return result;
  },

  impersonateUser: async (userId) => {
    return await amlApi.impersonateUser(userId);
  },

  setShowForm: (form) => set({ showForm: form }),
  setShowImport: (show) => set({ showImport: show }),
  clearError: () => set({ error: null }),
}));
